/**
 * PWA (Progressive Web App) utility functions
 * Handles PWA detection, install prompts, and platform-specific instructions
 */

// Store the beforeinstallprompt event for later use
let deferredPrompt: BeforeInstallPromptEvent | null = null;

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

// Capture the install prompt event when available
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e: Event) => {
    e.preventDefault();
    deferredPrompt = e as BeforeInstallPromptEvent;
  });
}

/**
 * Check if the app is running as an installed PWA (standalone mode)
 */
export function isPWAInstalled(): boolean {
  // Check for standalone display mode
  if (window.matchMedia('(display-mode: standalone)').matches) {
    return true;
  }

  // iOS Safari standalone mode
  if ((navigator as any).standalone === true) {
    return true;
  }

  // Check if launched from home screen on Android
  if (document.referrer.includes('android-app://')) {
    return true;
  }

  return false;
}

/**
 * Check if the native install prompt is available
 * (Chrome/Edge on Android, Chrome/Edge on desktop)
 */
export function hasNativeInstallPrompt(): boolean {
  return deferredPrompt !== null;
}

/**
 * Trigger the native install prompt
 * Returns true if accepted, false if dismissed
 */
export async function showNativeInstallPrompt(): Promise<boolean> {
  if (!deferredPrompt) {
    return false;
  }

  await deferredPrompt.prompt();
  const { outcome } = await deferredPrompt.userChoice;
  deferredPrompt = null;

  return outcome === 'accepted';
}

/**
 * Detect the current platform for PWA installation
 */
export type PWAPlatform = 'ios' | 'android' | 'desktop' | 'unknown';

export function detectPWAPlatform(): PWAPlatform {
  const userAgent = navigator.userAgent.toLowerCase();

  // iOS detection
  if (/iphone|ipad|ipod/.test(userAgent)) {
    return 'ios';
  }

  // Android detection
  if (/android/.test(userAgent)) {
    return 'android';
  }

  // Desktop detection (Windows, Mac, Linux)
  if (/windows|macintosh|linux/.test(userAgent) && !/android/.test(userAgent)) {
    return 'desktop';
  }

  return 'unknown';
}

/**
 * Check if the browser supports PWA installation
 */
export function canInstallPWA(): boolean {
  // Already installed
  if (isPWAInstalled()) {
    return false;
  }

  // Has native prompt (Chrome/Edge)
  if (hasNativeInstallPrompt()) {
    return true;
  }

  // iOS Safari can add to home screen
  const platform = detectPWAPlatform();
  if (platform === 'ios') {
    return true;
  }

  // Android can use native prompt
  if (platform === 'android') {
    return true;
  }

  // Desktop Chrome/Edge/Firefox
  if (platform === 'desktop') {
    return true;
  }

  return false;
}

/**
 * Get platform-specific installation instructions
 */
export interface PWAInstallInstructions {
  title: string;
  steps: string[];
  note?: string;
}

export function getPWAInstallInstructions(): PWAInstallInstructions {
  const platform = detectPWAPlatform();

  switch (platform) {
    case 'ios':
      return {
        title: 'Add to Home Screen',
        steps: [
          'Tap the Share button at the bottom of Safari',
          'Scroll down and tap "Add to Home Screen"',
          'Tap "Add" in the top right corner'
        ],
        note: 'The app will appear on your home screen like a regular app.'
      };

    case 'android':
      if (hasNativeInstallPrompt()) {
        return {
          title: 'Install App',
          steps: [
            'Tap the "Install" button below',
            'Confirm the installation when prompted'
          ],
          note: 'The app will be added to your home screen.'
        };
      }
      return {
        title: 'Add to Home Screen',
        steps: [
          'Tap the menu button (three dots) in Chrome',
          'Tap "Add to Home screen"',
          'Tap "Add" to confirm'
        ],
        note: 'The app will appear on your home screen.'
      };

    case 'desktop':
      if (hasNativeInstallPrompt()) {
        return {
          title: 'Install App',
          steps: [
            'Click the "Install" button below',
            'Confirm the installation when prompted'
          ],
          note: 'The app will open in its own window.'
        };
      }
      return {
        title: 'Install App',
        steps: [
          'Click the install icon in the address bar (right side)',
          'Or click the menu (three dots) and select "Install..."',
          'Confirm the installation'
        ],
        note: 'Works best in Chrome, Edge, or Brave.'
      };

    default:
      return {
        title: 'Install App',
        steps: [
          'Look for an "Install" or "Add to Home Screen" option in your browser menu'
        ]
      };
  }
}

/**
 * Check if we should show the PWA prompt to the user
 * @param pwaInstalled - from user profile
 * @param pwaPromptDisabled - from user profile
 */
export function shouldShowPWAPrompt(
  pwaInstalled: boolean,
  pwaPromptDisabled: boolean
): boolean {
  // Don't show if already installed or prompt disabled
  if (pwaInstalled || pwaPromptDisabled) {
    return false;
  }

  // Don't show if already running as PWA
  if (isPWAInstalled()) {
    return false;
  }

  // Only show on mobile (iOS/Android)
  const platform = detectPWAPlatform();
  if (platform !== 'ios' && platform !== 'android') {
    return false;
  }

  return true;
}
